import { Stack, Grid, Paper, Skeleton, Group, Box, Divider } from '@mantine/core';

const AvailableCoursesSkeleton = () => (
  <Paper
    withBorder
    p='md'
    radius='md'
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e9ecef'
    }}
  >
    <Stack gap='md'>
      <Group justify='space-between' align='flex-start'>
        <Stack gap={4}>
          <Skeleton height={18} width={140} />
          <Skeleton height={12} width={90} />
        </Stack>
        <Skeleton height={22} width={30} radius='xl' />
      </Group>

      {/* Search input */}
      <Skeleton height={36} radius='sm' />

      {/* Course cards */}
      <Stack gap='xs'>
        {[1, 2, 3, 4, 5].map((i) => (
          <Paper key={i} p='sm' withBorder radius='sm' style={{ border: '1px solid #e9ecef' }}>
            <Stack gap='xs'>
              <Group justify='space-between' align='flex-start'>
                <Stack gap={4} style={{ flex: 1 }}>
                  <Skeleton height={14} width='85%' />
                  <Skeleton height={12} width={60} />
                </Stack>
                <Skeleton height={16} width={16} radius='sm' />
              </Group>
              <Group gap='xs'>
                <Skeleton height={20} width={40} radius='xl' />
                <Skeleton height={20} width={64} radius='xl' />
              </Group>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Stack>
  </Paper>
);

const CourseScheduleSkeleton = () => (
  <Paper
    withBorder
    p='md'
    radius='md'
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e9ecef'
    }}
  >
    <Stack gap='md'>
      <Group justify='space-between' align='center'>
        <Skeleton height={22} width={180} />
        <Group gap='xs'>
          <Skeleton height={32} width={100} radius='sm' />
          <Skeleton height={32} width={110} radius='sm' />
        </Group>
      </Group>

      {/* Course groups */}
      {[1, 2, 3].map((i) => (
        <Paper
          key={i}
          withBorder
          radius='md'
          style={{ border: '1px solid #dee2e6', overflow: 'hidden' }}
        >
          {/* Course group header */}
          <Box p='md' style={{ backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
            <Group justify='space-between' align='center'>
              <Stack gap='xs' style={{ flex: 1 }}>
                <Skeleton height={16} width='55%' />
                <Group gap='xs'>
                  <Skeleton height={20} width={60} radius='xl' />
                  <Skeleton height={20} width={40} radius='xl' />
                  <Skeleton height={20} width={56} radius='xl' />
                </Group>
              </Stack>
              <Group gap='xs'>
                <Skeleton height={22} width={80} radius='xl' />
                <Skeleton height={28} width={28} radius='sm' />
                <Skeleton height={28} width={28} radius='sm' />
              </Group>
            </Group>
          </Box>

          {/* Course instance rows */}
          <Box p='md'>
            <Stack gap='sm'>
              {[1, 2].map((j) => (
                <Paper key={j} p='md' withBorder radius='sm'>
                  <Group justify='space-between' align='flex-start'>
                    <Stack gap={4} style={{ flex: 2 }}>
                      <Skeleton height={14} width='75%' />
                      <Skeleton height={12} width='50%' />
                    </Stack>
                    <Stack gap={4} style={{ flex: 2 }}>
                      <Skeleton height={12} width={110} />
                      <Skeleton height={12} width={90} />
                    </Stack>
                    <Stack gap={4} style={{ flex: 2 }}>
                      <Skeleton height={12} width={110} />
                      <Skeleton height={12} width={90} />
                    </Stack>
                    <Skeleton height={22} width={70} radius='xl' />
                    <Skeleton height={28} width={28} radius='sm' />
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Paper>
      ))}
    </Stack>
  </Paper>
);

const PlanningDetailsSkeleton = () => (
  <Paper
    withBorder
    p='lg'
    radius='md'
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e9ecef'
    }}
  >
    <Stack gap='md'>
      <Group justify='space-between' align='center'>
        <Skeleton height={22} width={140} />
        <Skeleton height={22} width={70} radius='xl' />
      </Group>

      <Divider />

      {/* Timeline section */}
      <Stack gap='xs'>
        <Skeleton height={16} width={80} />
        <Group gap='md'>
          <Stack gap={4} style={{ flex: 1 }}>
            <Skeleton height={12} width='80%' />
            <Skeleton height={16} width='90%' />
          </Stack>
          <Stack gap={4} style={{ flex: 1 }}>
            <Skeleton height={12} width='80%' />
            <Skeleton height={16} width='90%' />
          </Stack>
        </Group>
      </Stack>

      {/* Target Positions */}
      <Stack gap={4}>
        <Skeleton height={16} width={120} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} height={14} width={`${85 - i * 10}%`} />
        ))}
      </Stack>

      <Divider />

      {/* Total Budget */}
      <Stack gap={4}>
        <Skeleton height={12} width={100} />
        <Skeleton height={22} width={130} radius='xl' />
      </Stack>

      <Divider />

      {/* Assessment Dates */}
      <Stack gap='xs'>
        <Skeleton height={12} width={160} />
        <Group gap='xs'>
          <Skeleton height={36} style={{ flex: 2 }} radius='sm' />
          <Skeleton height={36} style={{ flex: 1 }} radius='sm' />
        </Group>
        <Skeleton height={12} width={140} />
        <Group gap='xs'>
          <Skeleton height={36} style={{ flex: 2 }} radius='sm' />
          <Skeleton height={36} style={{ flex: 1 }} radius='sm' />
        </Group>
      </Stack>

      <Divider />

      {/* Summary badges */}
      <Stack gap='xs'>
        <Skeleton height={16} width={80} />
        <Group gap='xs'>
          <Skeleton height={22} width={80} radius='xl' />
          <Skeleton height={22} width={90} radius='xl' />
        </Group>
      </Stack>

      <Divider />

      {/* Action buttons */}
      <Stack gap='xs'>
        <Skeleton height={36} radius='sm' />
        <Skeleton height={36} radius='sm' />
      </Stack>
    </Stack>
  </Paper>
);

const DetailCourseScheduleSkeleton = () => (
  <Paper
    withBorder
    p='md'
    radius='md'
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e9ecef'
    }}
  >
    <Stack gap='md'>
      <Group justify='space-between'>
        <Skeleton height={20} width={160} />
        <Skeleton height={32} width={200} radius='sm' />
      </Group>
      <Group justify='space-between'>
        <Skeleton height={36} width={240} radius='sm' />
        <Skeleton height={36} width={180} radius='sm' />
      </Group>
      <Stack gap='xs'>
        {/* Table header */}
        <Paper p='sm' withBorder>
          <Group gap='xs'>
            {[200, 150, 150, 80, 120, 100, 100, 60].map((w, i) => (
              <Skeleton key={i} height={12} width={w} />
            ))}
          </Group>
        </Paper>
        {/* Table rows */}
        {[1, 2, 3, 4, 5].map((i) => (
          <Paper key={i} p='sm' withBorder>
            <Group justify='space-between'>
              <Stack gap={4}>
                <Skeleton height={14} width={160} />
                <Skeleton height={12} width={80} />
              </Stack>
              <Skeleton height={14} width={120} />
              <Skeleton height={14} width={130} />
              <Skeleton height={14} width={50} />
              <Skeleton height={14} width={80} />
              <Skeleton height={22} width={70} radius='xl' />
              <Skeleton height={22} width={80} radius='xl' />
              <Skeleton height={28} width={28} radius='sm' />
            </Group>
          </Paper>
        ))}
      </Stack>
    </Stack>
  </Paper>
);

const DetailSidebarSkeleton = () => (
  <Paper
    withBorder
    p='lg'
    radius='md'
    style={{
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
      border: '1px solid #e9ecef'
    }}
  >
    <Stack gap='md'>
      <Group justify='space-between'>
        <Skeleton height={20} width={140} />
        <Skeleton height={22} width={70} radius='xl' />
      </Group>

      <Divider />

      {/* Timeline */}
      <Box>
        <Skeleton height={16} width={80} mb='xs' />
        <Grid>
          <Grid.Col span={6}>
            <Skeleton height={12} width={90} mb={6} />
            <Skeleton height={16} width={100} />
          </Grid.Col>
          <Grid.Col span={6}>
            <Skeleton height={12} width={80} mb={6} />
            <Skeleton height={16} width={100} />
          </Grid.Col>
        </Grid>
      </Box>

      {/* Summary badges */}
      <Box>
        <Skeleton height={16} width={80} mb='xs' />
        <Group gap='xs'>
          <Skeleton height={22} width={80} radius='xl' />
          <Skeleton height={22} width={90} radius='xl' />
        </Group>
      </Box>

      {/* Target Positions */}
      <Box>
        <Skeleton height={16} width={130} mb='xs' />
        <Stack gap={4}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={14} width='90%' />
          ))}
        </Stack>
      </Box>

      <Divider />

      {/* Course status breakdown */}
      <Box>
        <Skeleton height={16} width={130} mb='xs' />
        <Stack gap={6}>
          {[1, 2, 3].map((i) => (
            <Paper key={i} p='xs' withBorder>
              <Group justify='space-between'>
                <Skeleton height={12} width={80} />
                <Skeleton height={14} width={30} />
              </Group>
            </Paper>
          ))}
        </Stack>
      </Box>
    </Stack>
  </Paper>
);

export const SchedulePlanDetailSkeleton = () => (
  <Stack gap='md'>
    {/* Header breadcrumb + actions */}
    <Group justify='space-between' align='flex-start'>
      <Skeleton height={16} width={280} />
      <Group gap='sm'>
        <Skeleton height={36} width={160} radius='sm' />
        <Skeleton height={36} width={130} radius='sm' />
      </Group>
    </Group>

    <Grid gutter='lg'>
      <Grid.Col span={8}>
        <Stack gap='md'>
          {/* Planning Overview card */}
          <Paper
            withBorder
            p='md'
            radius='md'
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid #e9ecef'
            }}
          >
            <Skeleton height={24} width='55%' mb='md' />
            <Skeleton height={22} width={100} radius='xl' mb='md' />
            <Stack gap='xs' mb='md'>
              <Skeleton height={14} />
              <Skeleton height={14} width='85%' />
            </Stack>
            <Group gap='md'>
              <Skeleton height={22} width={90} radius='xl' />
              <Skeleton height={14} width={160} />
            </Group>
          </Paper>

          <DetailCourseScheduleSkeleton />
        </Stack>
      </Grid.Col>

      <Grid.Col span={4}>
        <DetailSidebarSkeleton />
      </Grid.Col>
    </Grid>
  </Stack>
);

export const SchedulePlanEditSkeleton = () => (
  <Stack gap='md'>
    {/* Header breadcrumb + actions */}
    <Group justify='space-between' align='flex-start'>
      <Skeleton height={16} width={280} />
      <Skeleton height={36} width={220} radius='sm' />
    </Group>

    <Grid gutter='lg'>
      <Grid.Col span={3}>
        <AvailableCoursesSkeleton />
      </Grid.Col>

      <Grid.Col span={6}>
        <CourseScheduleSkeleton />
      </Grid.Col>

      <Grid.Col span={3}>
        <PlanningDetailsSkeleton />
      </Grid.Col>
    </Grid>
  </Stack>
);
